import type { ProposerFieldPermissions } from '@composite/proposers/proposer-field';
import { CourtFile, Me } from '@models';
import { Agenda, EsFileState, Role } from '@enums';

export type GetProposerFieldPermissionsParams = {
  esFile?: CourtFile | null;
  user?: Me | null;
  isExisting?: boolean;
  baseCanWriteProposers?: boolean;
};

const restrictedContactRoles = [
  Role.ANALYST,
  Role.ANONYMIZATION,
  Role.PUBLICATION,
  Role.ADVISER,
];

const typeDistinctionRoles = [Role.ANALYST, Role.PUBLICATION];

const typeDistinctionWorkingStates = [
  EsFileState.JUDGE_OFFICE,
  EsFileState.REGISTER,
  EsFileState.CLOSED,
];

const hasUserRole = (user: Me | null | undefined, roles: Role[]) =>
  (user?.userRoles ?? []).some(({ role }) => role && roles.includes(role));

export function getProposerFieldPermissions({
  esFile,
  user,
  baseCanWriteProposers = false,
}: GetProposerFieldPermissionsParams): ProposerFieldPermissions | undefined {
  const isSuperAdmin = hasUserRole(user, [Role.SUPERADMIN]);

  if (isSuperAdmin) {
    return undefined;
  }

  const hasRestrictedContactRole = hasUserRole(user, restrictedContactRoles);

  if (!hasRestrictedContactRole || !baseCanWriteProposers) {
    return undefined;
  }

  if (esFile?.agenda !== Agenda.COURT) {
    return undefined;
  }

  const canWriteTypeAndDistinction =
    !!esFile?.state &&
    typeDistinctionWorkingStates.includes(esFile.state) &&
    hasUserRole(user, typeDistinctionRoles);

  return {
    contact: false,
    type: canWriteTypeAndDistinction,
    distinction: canWriteTypeAndDistinction,
  };
}
